import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';

/**
 * Service pour gérer les marqueurs d'agence
 */
class AgencyMarkerService {
  /**
   * Géocode une adresse et met à jour les coordonnées du marqueur agence
   * @param agencyId ID de l'agence
   * @param address Adresse à géocoder
   * @returns Promise avec le résultat de la mise à jour
   */
  async geocodeAndUpdateAgencyMarker(agencyId: string, address: string): Promise<{ success: boolean; error?: string }> {
    try {
      logger.info(`Géocodage de l'adresse de l'agence ${agencyId}: ${address}`);
      
      // Utiliser l'API Mapbox pour géocoder l'adresse
      const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
      if (!mapboxToken) {
        throw new Error('Token Mapbox non défini');
      }
      
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxToken}&country=fr&language=fr&limit=1`,
        {
          headers: {
            'Accept-Language': 'fr'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Erreur lors du géocodage: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        throw new Error('Aucune coordonnée trouvée pour cette adresse');
      }
      
      const [longitude, latitude] = data.features[0].center;
      logger.info(`Coordonnées trouvées: [${longitude}, ${latitude}]`);
      
      // Mettre à jour le marqueur agence avec les coordonnées
      const updateQuery = `
        UPDATE markers 
        SET coordinates = ST_SetSRID(ST_MakePoint(${longitude}, ${latitude}), 4326)::geography,
            updated_at = NOW()
        WHERE agency_id = '${agencyId}' 
          AND is_agency_marker = TRUE
        RETURNING id;
      `;
      
      const { error: updateError } = await supabase.rpc(
        'run_sql_query',
        { sql_query: updateQuery }
      );
      
      if (updateError) {
        logger.error('Erreur lors de la mise à jour du marqueur agence:', updateError);
        throw updateError;
      }
      
      logger.info('Marqueur agence mis à jour avec succès');
      return { success: true };
    } catch (error: any) {
      logger.error('Exception lors du géocodage et de la mise à jour du marqueur agence:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Vérifie si un marqueur agence existe pour une agence donnée
   * @param agencyId ID de l'agence
   * @returns Promise avec le résultat de la vérification
   */
  async checkAgencyMarkerExists(agencyId: string): Promise<{ exists: boolean; hasCoordinates: boolean }> {
    try {
      const { data, error } = await supabase
        .from('markers')
        .select('id, coordinates')
        .eq('agency_id', agencyId)
        .eq('is_agency_marker', true)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Aucun marqueur trouvé
          return { exists: false, hasCoordinates: false };
        }
        throw error;
      }
      
      return {
        exists: true,
        hasCoordinates: data.coordinates !== null
      };
    } catch (error: any) {
      logger.error('Exception lors de la vérification du marqueur agence:', error);
      return { exists: false, hasCoordinates: false };
    }
  }
  
  /**
   * Crée un marqueur agence si il n'existe pas
   * @param agencyId ID de l'agence
   * @param userId ID de l'utilisateur propriétaire
   * @param address Adresse de l'agence
   * @param agencyName Nom de l'agence
   * @returns Promise avec le résultat de la création
   */
  async createAgencyMarkerIfNotExists(
    agencyId: string,
    userId: string,
    address: string,
    agencyName: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Vérifier si le marqueur existe déjà
      const { exists } = await this.checkAgencyMarkerExists(agencyId);
      
      if (exists) {
        logger.info(`Marqueur agence existe déjà pour l'agence ${agencyId}`);
        return { success: true };
      }
      
      logger.info(`Création d'un marqueur agence pour l'agence ${agencyId}`);
      
      // Récupérer ou créer la catégorie "Agence"
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('id')
        .ilike('name', 'agence')
        .single();
      
      if (categoryError && categoryError.code !== 'PGRST116') {
        throw categoryError;
      }
      
      let categoryId: string;
      
      if (!categoryData) {
        // Créer la catégorie "Agence"
        const { data: newCategory, error: createCategoryError } = await supabase
          .from('categories')
          .insert({
            name: 'Agence',
            color: '#9b59b6',
            icon: 'FaBuilding'
          })
          .select('id')
          .single();
        
        if (createCategoryError) {
          throw createCategoryError;
        }
        
        categoryId = newCategory.id;
      } else {
        categoryId = categoryData.id;
      }
      
      // Géocoder l'adresse
      const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
      if (!mapboxToken) {
        throw new Error('Token Mapbox non défini');
      }
      
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxToken}&country=fr&language=fr&limit=1`,
        {
          headers: {
            'Accept-Language': 'fr'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Erreur lors du géocodage: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        throw new Error('Aucune coordonnée trouvée pour cette adresse');
      }
      
      const [longitude, latitude] = data.features[0].center;
      
      // Créer le marqueur agence avec les coordonnées
      const insertQuery = `
        INSERT INTO markers (
          coordinates,
          address,
          category_id,
          agency_id,
          user_id,
          is_agency_marker,
          description,
          created_at,
          updated_at
        ) VALUES (
          ST_SetSRID(ST_MakePoint(${longitude}, ${latitude}), 4326)::geography,
          '${address.replace(/'/g, "''")}',
          '${categoryId}',
          '${agencyId}',
          '${userId}',
          TRUE,
          'Marqueur de l''agence ${agencyName.replace(/'/g, "''")}',
          NOW(),
          NOW()
        )
        RETURNING id;
      `;
      
      const { error: insertError } = await supabase.rpc(
        'run_sql_query',
        { sql_query: insertQuery }
      );
      
      if (insertError) {
        logger.error('Erreur lors de la création du marqueur agence:', insertError);
        throw insertError;
      }
      
      logger.info('Marqueur agence créé avec succès');
      return { success: true };
    } catch (error: any) {
      logger.error('Exception lors de la création du marqueur agence:', error);
      return { success: false, error: error.message };
    }
  }
}

// Exporter une instance unique du service
export const agencyMarkerService = new AgencyMarkerService();
